import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useUser } from '@clerk/clerk-react';
import { api } from '../lib/api';
import StaffApprovalModal from './admin/StaffApprovalModal';
import NotificationsModal from './notifications/NotificationsModal';
import { showAlert, showConfirm, showPrompt } from './common/AppDialogs';

interface EmailActionHandlerProps {
  children: React.ReactNode;
}

export default function EmailActionHandler({ children }: EmailActionHandlerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoaded } = useUser();
  
  const [showStaffApprovalModal, setShowStaffApprovalModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const [currentJoinRequest, setCurrentJoinRequest] = useState<any>(null);
  const [processedActions, setProcessedActions] = useState<Set<string>>(new Set());


  // Get URL parameters from window.location
  const urlParams = new URLSearchParams(window.location.search);
  const action = urlParams.get('action');
  const requestId = urlParams.get('requestId');
  const organizationId = urlParams.get('organizationId');
  const notificationId = urlParams.get('notificationId');
  const invitationToken = urlParams.get('token');

  // Fetch join request details - using dynamic URL params
  const { data: joinRequestData } = useQuery({
    queryKey: ['join-request', requestId],
    queryFn: () => {
      const currentRequestId = new URLSearchParams(window.location.search).get('requestId');
      if (!currentRequestId) throw new Error('No request ID found');
      return api.get(`/organizations/join-requests/${currentRequestId}`).then(res => res.data.data);
    },
    enabled: !!requestId && (action === 'approve' || action === 'reject'),
    onSuccess: (data) => {
      const currentAction = new URLSearchParams(window.location.search).get('action');
      if (data && currentAction === 'approve') {
        setCurrentJoinRequest(data);
        setShowStaffApprovalModal(true);
        // Clear URL params after modal opens successfully
        clearUrlParams();
      }
    }
  });

  // Fetch organization staff for approval modal
  const { data: organizationStaff = [] } = useQuery({
    queryKey: ['organization-staff'],
    queryFn: () => api.get('/organizations/staff').then(res => res.data.data),
    enabled: showStaffApprovalModal,
  });

  // Approve join request mutation
  const approveRequestMutation = useMutation({
    mutationFn: ({ requestId, staffData }: { requestId: string; staffData: any }) => {
      return api.post(`/organizations/join-requests/${requestId}/approve`, { staffData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      setShowStaffApprovalModal(false);
      setCurrentJoinRequest(null);
      clearUrlParams();
      console.log('✅ EMAIL ACTION: User approved successfully, URL params cleared');
      // Show success message or redirect
    },
    onError: (error) => {
      console.error('Failed to approve join request:', error);
      showAlert('Failed to approve join request. Please try again.');
    },
  });

  // Reject join request mutation
  const rejectRequestMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) => {
      return api.post(`/organizations/join-requests/${requestId}/reject`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications', 'unread-count']);
      clearUrlParams();
      // Show success message
    },
    onError: (error) => {
      console.error('Failed to reject join request:', error);
      showAlert('Failed to reject join request. Please try again.');
    },
  });

  // Accept invitation mutation
  const acceptInvitationMutation = useMutation({
    mutationFn: (token: string) => {
      return api.get(`/invitations/accept/${token}`);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['user']);
      clearUrlParams();
      
      // Redirect to organization workspace or homepage
      if (response.data.organizationId) {
        navigate('/');
      } else {
        navigate('/');
      }
      
      showAlert('Invitation accepted successfully! Welcome to the organization.');
    },
    onError: (error: any) => {
      console.error('Failed to accept invitation:', error);
      const message = error.response?.data?.error || 'Failed to accept invitation. Please try again.';
      showAlert(message);
      clearUrlParams();
    },
  });

  // Clear URL parameters after action
  const clearUrlParams = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('action');
    currentUrl.searchParams.delete('requestId');
    currentUrl.searchParams.delete('organizationId');
    currentUrl.searchParams.delete('notificationId');
    currentUrl.searchParams.delete('token');
    
    // Update URL without page reload
    window.history.replaceState({}, '', currentUrl.toString());
  };

  // Handle URL parameters on mount and changes
  useEffect(() => {
    const handleEmailActions = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const currentAction = urlParams.get('action');
      const currentRequestId = urlParams.get('requestId');
      const currentOrganizationId = urlParams.get('organizationId');
      const currentNotificationId = urlParams.get('notificationId');
      const currentInvitationToken = urlParams.get('token');
      
      if (!currentAction) {
        return;
      }

      // Create unique action key to prevent duplicate processing
      const actionKey = `${currentAction}-${currentRequestId || currentNotificationId || currentInvitationToken}`;
      
      if (processedActions.has(actionKey)) {
        return;
      }


      // Mark action as processed
      setProcessedActions(prev => new Set(prev).add(actionKey));

      switch (currentAction) {
        case 'approve':
          if (currentRequestId && currentOrganizationId) {
            // The join request fetch will trigger the modal
            // Don't clear URL params yet - let the useQuery fetch first
          }
          break;
          
        case 'reject':
          if (currentRequestId && currentOrganizationId) {
            // Show confirmation dialog for rejection
            const confirmed = await showConfirm(
              'Are you sure you want to reject this join request? This action cannot be undone.'
            );
            if (confirmed) {
              const reason = await showPrompt('Optional: Provide a reason for rejection:');
              rejectRequestMutation.mutate({ requestId: currentRequestId, reason: reason || undefined });
            }
            // Clear URL params after handling rejection
            clearUrlParams();
          }
          break;
          
        case 'review':
          if (currentNotificationId) {
            setSelectedNotificationId(currentNotificationId);
            setShowNotificationsModal(true);
            // Clear URL params after opening notifications modal
            clearUrlParams();
          }
          break;
          
        case 'accept_invitation':
          console.log('🎫 Processing accept_invitation action', { currentInvitationToken, isLoaded, user: !!user });
          if (currentInvitationToken) {
            // Check if user is authenticated
            if (!isLoaded) {
              console.log('⏳ Waiting for Clerk to load');
              // Wait for Clerk to load
              return;
            }
            
            if (!user) {
              console.log('👤 User not authenticated - storing token and redirecting');
              // User is not authenticated - store the token and redirect to sign-in
              localStorage.setItem('nexus_pending_invitation_token', currentInvitationToken);
              localStorage.setItem('nexus_onboarding_intent', 'invitation');
              
              // Navigate to homepage which will trigger the register modal
              console.log('🏠 Navigating to homepage with show_register=true');
              navigate('/?show_register=true');
              clearUrlParams();
            } else {
              console.log('✅ User authenticated - processing invitation');
              // User is authenticated - process the invitation
              acceptInvitationMutation.mutate(currentInvitationToken);
            }
          } else {
            console.log('❌ No invitation token found');
          }
          break;
          
        default:
          console.warn('Unknown email action:', currentAction);
          clearUrlParams();
      }
    };

    // Handle on mount
    handleEmailActions();

    // Listen for navigation changes (if using react-router)
    const handlePopState = () => {
      handleEmailActions();
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Empty dependency array - only run on mount

  return (
    <>
      {children}
      
      {/* Staff Approval Modal for email approve action */}
      {showStaffApprovalModal && currentJoinRequest && (
        <StaffApprovalModal
          isOpen={showStaffApprovalModal}
          onClose={() => {
            setShowStaffApprovalModal(false);
            setCurrentJoinRequest(null);
            clearUrlParams();
          }}
          onApprove={(staffData) => {
            approveRequestMutation.mutate({
              requestId: currentJoinRequest.id,
              staffData
            });
          }}
          onReject={(reason) => {
            rejectRequestMutation.mutate({
              requestId: currentJoinRequest.id,
              reason
            });
          }}
          joinRequest={currentJoinRequest}
          existingStaff={organizationStaff}
          isLoading={approveRequestMutation.isLoading || rejectRequestMutation.isLoading}
        />
      )}
      
      {/* Notifications Modal for email review action */}
      {showNotificationsModal && (
        <NotificationsModal
          isOpen={showNotificationsModal}
          onClose={() => {
            setShowNotificationsModal(false);
            setSelectedNotificationId(null);
          }}
          // TODO: Add prop to pre-select specific notification
          selectedNotificationId={selectedNotificationId}
        />
      )}
    </>
  );
}
